import React, { useState, useContext, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  ArrowLeft,
  Moon,
  Sun,
  MessageSquare,
  UserCircle,
  PanelLeft,
  PanelTop,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { cn } from "../lib/utils";

import MainListItems from "./MainListItems";
import TopNav from "./TopNav";
import BottomNav from "./BottomNav";
import MenuDeIcones from "./MenuDeIcones";
import { lerPreferencia, gravarPreferencia } from "../services/preferencias";
import InstalarApp from "../components/InstalarApp";
import AtualizacaoDisponivel from "../components/AtualizacaoDisponivel";
import AvisoDeTempoReal from "../components/AvisoDeTempoReal";
import NotificationsPopOver from "../components/NotificationsPopOver";
import AvisosDeVenda, { useAvisosDeVenda } from "../components/AvisosDeVenda";
import GlobalSearch from "../components/GlobalSearch";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { useWhiteLabel } from "../context/WhiteLabel";
import PainelDeAparencia from "../components/Aparencia/Painel";
import useVersao from "../hooks/useVersao";
import useModules from "../hooks/useModules";
import {
  ITENS_DO_PLANO_STATUS,
  ehSiteDeStatus,
  telaDoPlanoStatus,
} from "../helpers/planoStatus";

// Atalhos da barra de baixo no plano "só Status" (o "Mais" completa o resto).
const ATALHOS_DO_PLANO_STATUS = ITENS_DO_PLANO_STATUS.slice(0, 4).map(
  ({ to, icon, label }) => ({ to, Icone: icon, rotulo: label })
);

// 264 e não 240: com o recuo dos subitens, rótulos como "Cargos e permissões"
// e "Mensagens agendadas" chegavam ao limite e eram cortados com reticências.
// Nome de menu truncado obriga a adivinhar para onde o item leva.
const SIDEBAR_WIDTH = 264;
const SIDEBAR_WIDTH_COLLAPSED = 72;

// Onde o menu mora, no desktop: "lateral" (padrão, como sempre foi) ou "topo".
// Vai em cookie porque é assim que este projeto guarda preferência de tela —
// o localStorage saiu de propósito (ver services/preferencias.js).
const CHAVE_MODO_MENU = "layout:modoMenu";

function SidebarContent({ collapsed, drawerClose, onAlternar }) {
  // A marca da empresa, não a nossa: quem configurou nome e logo espera
  // vê-los aqui, e não "OrbeChat V.1" fixo.
  // `logoDoTema` troca sozinha no modo escuro; `iconeQuadrado` é o símbolo.
  const { appName, logoDoTema, iconeQuadrado, logoOnly } = useWhiteLabel();

  // A logo INTEIRA, com a altura travada e a largura livre: forçá-la num
  // quadrado era o que fazia a logo horizontal aparecer como se fosse um ícone.
  // Recolhido cabe só o símbolo (logo deitada num quadrado de 32px vira
  // borrão), e o sanduíche desce para baixo dele.
  const marca = collapsed ? iconeQuadrado : logoDoTema || iconeQuadrado;

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          "flex items-center gap-2",
          collapsed ? "flex-col gap-1 px-2 py-3" : "h-14 px-4"
        )}
      >
        {marca ? (
          <img
            src={marca}
            alt={appName}
            className={
              collapsed
                ? "h-8 w-8 shrink-0 rounded-lg object-contain"
                : "h-8 w-auto min-w-0 max-w-[11rem] shrink object-contain object-left"
            }
          />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <MessageSquare className="h-4 w-4" />
          </div>
        )}
        {/* `logoOnly`: quem tem logo com o nome desenhado dentro não quer o
            nome escrito de novo ao lado. Sem logo o nome fica, senão o topo
            do menu ficaria vazio. */}
        {!collapsed && !(logoOnly && logoDoTema) && (
          <span className="truncate text-base font-semibold tracking-tight">
            {appName}
          </span>
        )}
        {/* Mostrar/esconder a lateral mora nela, ao lado da logo. Antes era
            uma seta (‹) na barra de cima, lida como "voltar". */}
        <Button
          variant="ghost"
          size="icon"
          className={cn("shrink-0", !collapsed && "ml-auto")}
          aria-label={collapsed ? "Mostrar menu" : "Recolher menu"}
          title={collapsed ? "Mostrar menu" : "Recolher menu"}
          aria-expanded={!collapsed}
          onClick={onAlternar}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>
      <div className="scrollbar-discreta flex-1 overflow-y-auto py-2">
        <MainListItems drawerClose={drawerClose} collapsed={collapsed} />
      </div>
    </div>
  );
}

const LoggedInLayout = ({ children }) => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const { appName, logoDoTema, iconeQuadrado, logoOnly } = useWhiteLabel();
  const versao = useVersao();
  const { statusOnly, carregando: carregandoPlano } = useModules();
  const location = useLocation();
  const navigate = useNavigate();
  // Há tela do app antes desta? (`history.state.idx`, gravado pelo router;
  // 0 = entrou direto). Relido a cada navegação porque `location` muda.
  const podeVoltar = window.history.state?.idx > 0;
  const avisosDeVenda = useAvisosDeVenda(user?.id && !statusOnly);

  // Só vale do md: para cima. No celular a navegação é a barra de baixo mais a
  // bandeja de ícones, e nada disso muda com esta preferência.
  const [modoMenu, setModoMenu] = useState(() =>
    lerPreferencia(CHAVE_MODO_MENU, "lateral") === "topo" ? "topo" : "lateral"
  );
  const modoTopo = modoMenu === "topo";

  const alternarModoMenu = () => {
    setModoMenu((anterior) => {
      const novo = anterior === "topo" ? "lateral" : "topo";
      gravarPreferencia(CHAVE_MODO_MENU, novo);
      return novo;
    });
  };

  useEffect(() => {
    const checkSize = () => {
      // 768 e não 600: é o `md:` do Tailwind, o mesmo ponto em que a barra de
      // baixo some e a lateral aparece. Com 600 aqui, entre 600 e 767 a tela
      // ainda era a de celular mas `isMobile` já dizia que não — e o
      // `drawerClose` de cada item do menu não fechava mais a bandeja.
      const largura = document.body.offsetWidth;
      const mobile = largura < 768;
      setIsMobile(mobile);
      if (!mobile) {
        // No tablet (768-1023) a lateral aberta come 264px de uma tela que já
        // precisa caber lista + conversa: sobravam 184px para ler mensagem.
        // Ali ela nasce recolhida (72px, só ícones); quem quiser abre no
        // botão. Do desktop para cima volta a abrir sozinha.
        setDrawerOpen(largura >= 1024);
        // A bandeja é `md:hidden`: virando desktop com ela aberta, ela some da
        // vista mas o Radix continua segurando o scroll do body.
        setMobileOpen(false);
      }
    };
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  const drawerClose = () => {
    if (isMobile) setMobileOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
  };

  const handleClickLogout = () => {
    handleLogout();
  };

  // No status.* espera o plano antes de montar a tela: sem isso o Dashboard
  // chegava a abrir e disparar pedidos que o plano "só Status" barra.
  if (loading || (carregandoPlano && ehSiteDeStatus())) {
    return <BackdropLoading />;
  }

  // Tela fora do plano "só Status" (o Dashboard do login, um link antigo):
  // vai para o Status em vez de abrir e tomar 403 em tudo.
  if (statusOnly && !telaDoPlanoStatus(location.pathname)) {
    return <Navigate to="/status-posts" replace />;
  }

  const sidebarWidth = drawerOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED;

  return (
    // h-dvh, não h-screen: no celular o 100vh do `h-screen` conta a área que
    // a barra de endereço do navegador ocupa, então o rodapé da tela — a
    // caixa de digitar mensagem — ficava abaixo da parte visível e aparecia
    // cortado. O dvh acompanha a barra aparecendo e sumindo.
    // `relative`: sem ancestral posicionado, todo `absolute` sem dono (os
    // `sr-only` dos KpiCard, por exemplo) se mede pela janela e não é cortado
    // pelo `overflow-hidden`, e o documento ganha uma segunda rolagem.
    <div className="relative flex h-dvh w-full overflow-hidden bg-background">
      {/* Barra lateral do desktop. No modo "topo" ela não é escondida por CSS:
          sai da árvore mesmo, senão o MainListItems continuaria montado e o
          conteúdo não ganharia a largura toda. */}
      {!modoTopo && (
        <aside
          className={cn(
            "hidden shrink-0 border-r bg-card transition-[width] duration-200 ease-in-out md:block"
          )}
          style={{ width: sidebarWidth }}
        >
          <SidebarContent
            collapsed={!drawerOpen}
            drawerClose={drawerClose}
            onAlternar={() => setDrawerOpen((prev) => !prev)}
          />
        </aside>
      )}

      {/* No celular não há gaveta lateral: o "Mais" da barra de baixo abre
          uma bandeja com todos os ícones. Gaveta que entra pelo lado é padrão
          de desktop — cobre o conteúdo por uma borda só e obriga a percorrer
          38 linhas verticais para achar uma tela. */}
      <MenuDeIcones
        aberto={mobileOpen}
        aoFechar={() => setMobileOpen(false)}
        aoAbrirPerfil={handleOpenUserModal}
        aoSair={handleClickLogout}
        avisosDeVenda={avisosDeVenda}
      />

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
        meuPerfil
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        {/* pt com a safe area: aberto como app na tela de início, o topo cai
            atrás do relógio e da bateria do iPhone. */}
        {/* Barra de cima: só do tablet para cima.
            No celular ela custava 56px de altura em toda tela para carregar
            um menu que agora é a barra de baixo e ações de conta que passaram
            para a bandeja do "Mais". Busca e avisos foram para a barra de
            baixo, que é onde o polegar alcança. */}
        <header className="hidden min-h-14 shrink-0 items-center gap-3 border-b bg-background px-4 pt-[env(safe-area-inset-top)] md:flex">
          {/* O menu completo, no celular.
              A barra de baixo cobre os quatro destinos do dia a dia, mas ela
              se recolhe dentro de uma conversa — e lá o menu ficava sem
              nenhuma porta de entrada. Este botão existe em toda tela, que é
              o que se espera de um sanduíche. */}
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 md:hidden"
            aria-label="Abrir menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          {/* Voltar à página anterior, como o botão do navegador — mas só
              dentro do sistema: sem tela do app antes, fica desligado em vez
              de levar a pessoa para fora. */}
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            aria-label="Voltar à página anterior"
            title="Voltar à página anterior"
            disabled={!podeVoltar}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          {/* A marca aparece no topo do celular: sem a barra lateral fixa, a
              tela não dizia em que sistema a pessoa está. No modo topo ela
              precisa aparecer no desktop também, pelo mesmo motivo: quem
              carregava logo e nome era o cabeçalho da lateral, que sumiu. */}
          {modoTopo && (
            <div className="hidden shrink-0 items-center gap-2 md:flex">
              {/* No topo há largura sobrando: a logo inteira, não o símbolo. */}
              {logoDoTema || iconeQuadrado ? (
                <img
                  src={logoDoTema || iconeQuadrado}
                  alt={appName}
                  className="h-7 w-auto max-w-[10rem] shrink-0 object-contain object-left"
                />
              ) : (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <MessageSquare className="h-4 w-4" />
                </div>
              )}
              {!(logoOnly && logoDoTema) && (
                <span className="hidden truncate text-base font-semibold tracking-tight lg:inline">
                  {appName}
                </span>
              )}
            </div>
          )}

          {/* No celular a barra de cima é o único lugar que diz onde a pessoa
              está: com `logoOnly` o nome dá lugar à própria logo. */}
          {logoOnly && logoDoTema ? (
            <img
              src={logoDoTema}
              alt={appName}
              className="h-7 max-w-[9rem] shrink-0 object-contain md:hidden"
            />
          ) : (
            <span className="truncate text-base font-semibold tracking-tight md:hidden">
              {appName}
            </span>
          )}

          {/* No modo topo a navegação ocupa o espaço livre da barra; no modo
              lateral, o espaço só empurra as ações para a direita. */}
          {modoTopo ? <TopNav /> : <div className="flex-1" />}

          {/* O par sol/lua com o interruptor ocupava três espaços da barra —
              no celular sobrava pouco para busca e avisos, que são o que se
              usa. Ali ele vira um item do menu do usuário. */}
          <div className="hidden shrink-0 items-center gap-1 md:flex">
            <Sun className="h-4 w-4 text-muted-foreground" />
            <Switch checked={darkMode} onCheckedChange={toggleTheme} />
            <Moon className="h-4 w-4 text-muted-foreground" />
          </div>

          {/* Busca e avisos são de atendimento: o plano "só Status" não tem. */}
          {user.id && !statusOnly && <GlobalSearch />}
          {user.id && !statusOnly && <NotificationsPopOver />}
          <AvisosDeVenda estado={avisosDeVenda} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <UserCircle className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="md:hidden"
                onSelect={(e) => {
                  // Sem isto o menu fecha antes de o tema trocar, e o toque
                  // parece não ter feito nada.
                  e.preventDefault();
                  toggleTheme();
                }}
              >
                {darkMode ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
                {darkMode ? "Tema claro" : "Tema escuro"}
              </DropdownMenuItem>
              {/* Onde o menu fica é preferência de aparência, da mesma família
                  do tema — e este já é o lugar do tema. Uma opção usada uma vez
                  na vida não merece espaço fixo na barra, e aqui ela fica no
                  mesmo canto em todas as telas. Só no desktop: no celular a
                  navegação é outra (barra de baixo + bandeja) e o item seria uma
                  chave que não liga nada. */}
              <DropdownMenuItem
                className="hidden md:flex"
                onSelect={(e) => {
                  // Igual ao tema: sem isto o menu fecha antes de a tela se
                  // remontar e o clique parece não ter feito nada.
                  e.preventDefault();
                  alternarModoMenu();
                }}
              >
                {modoTopo ? (
                  <PanelLeft className="h-4 w-4" />
                ) : (
                  <PanelTop className="h-4 w-4" />
                )}
                {modoTopo ? "Menu lateral" : "Menu no topo"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </DropdownMenuItem>
              {/* Qual versão está no ar. Fica no fim do menu do usuário porque
                  é o canto que existe em todas as telas — e é a primeira
                  pergunta de quem abre um chamado ("qual versão você está
                  usando?") ou acaba de fazer um deploy. */}
              {versao && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    versão {versao.versao}
                    {versao.commit ? ` · ${versao.commit}` : ""}
                    {versao.ambiente && versao.ambiente !== "production"
                      ? ` · ${versao.ambiente}`
                      : ""}
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* pt com a safe area no celular: sem a barra de cima, o conteúdo
            passou a começar na primeira linha da tela — e aberto como
            aplicativo, ou num iPhone com entalhe, a primeira linha fica atrás
            do relógio e da bateria. Do md: para cima quem já cuida disso é o
            cabeçalho. */}
        {/* Linha com o inspetor de aparência (components/Aparencia/Painel):
            do lg para cima ele é uma coluna ao lado e a página encolhe; no
            celular é folha flutuante e não ocupa lugar aqui. */}
        <div className="flex min-h-0 flex-1">
          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto pt-[env(safe-area-inset-top)] md:pt-0">
            {children ? children : null}
          </main>
          <PainelDeAparencia />
        </div>

        <InstalarApp />
        <AtualizacaoDisponivel />
        <AvisoDeTempoReal />

        <BottomNav
          onAbrirMenu={() => setMobileOpen(true)}
          avisosNaoLidos={avisosDeVenda.ligado ? avisosDeVenda.naoLidas : 0}
          {...(statusOnly && { itens: ATALHOS_DO_PLANO_STATUS })}
        />
      </div>
    </div>
  );
};

export default LoggedInLayout;
